import { isStaging } from '@/modules/firebase'

const LOCAL_URL = 'http://localhost:3005'
const STAGING_URL = (import.meta.env.VITE_LITTA_API_URL_STAGING as string) || ''

const LITTA_LOCAL_KEY = 'littaApi:useLocal'
const LITTA_OVERRIDE_KEY = 'littaApi:overrideUrl'

export function isLittaLocalEnabled(): boolean {
    return localStorage.getItem(LITTA_LOCAL_KEY) === 'true'
}

export function setLittaLocalEnabled(enabled: boolean): void {
    localStorage.setItem(LITTA_LOCAL_KEY, String(enabled))
}

/**
 * Requests to the override URL carry the signed-in user's Firebase ID token, so only
 * allow destinations we control: litta-api Cloud Run services in the Litta
 * staging project (owldxgx2oa-nw is that project's stable URL suffix) and
 * localhost. A bare litta-api prefix is not enough, as anyone can deploy a
 * run.app service with that name in their own project.
 */
const TRUSTED_OVERRIDE_HOST = /^litta-api(-pr-\d+)?-owldxgx2oa-nw\.a\.run\.app$/

export function isTrustedOverrideUrl(url: string): boolean {
    try {
        const parsed = new URL(url)
        if (
            parsed.hostname === 'localhost' ||
            parsed.hostname === '127.0.0.1'
        ) {
            return true
        }
        return (
            parsed.protocol === 'https:' &&
            TRUSTED_OVERRIDE_HOST.test(parsed.hostname)
        )
    } catch {
        return false
    }
}

export function getLittaApiOverrideUrl(): string | null {
    const url = localStorage.getItem(LITTA_OVERRIDE_KEY)?.trim()
    if (!url) return null
    const normalised = url.replace(/\/+$/, '')
    return isTrustedOverrideUrl(normalised) ? normalised : null
}

export function setLittaApiOverrideUrl(url: string | null): void {
    const trimmed = url?.trim().replace(/\/+$/, '')
    if (trimmed) {
        localStorage.setItem(LITTA_OVERRIDE_KEY, trimmed)
    } else {
        localStorage.removeItem(LITTA_OVERRIDE_KEY)
    }
}

export function getLittaPublicApiBaseUrl(): string {
    if (isStaging) {
        const override = getLittaApiOverrideUrl()
        if (override) return override
        if (isLittaLocalEnabled()) return LOCAL_URL
        if (STAGING_URL) return STAGING_URL
    }
    return import.meta.env.VITE_LITTA_API_URL as string
}

/** Staging vs production hosted API only — ignores the localhost override. */
export function getLittaPublicApiRemoteBaseUrl(): string {
    if (isStaging) {
        const override = getLittaApiOverrideUrl()
        if (override) return override
        if (STAGING_URL) return STAGING_URL
    }
    return import.meta.env.VITE_LITTA_API_URL as string
}
